import { isEmptyString, isNil, isNotNil } from '../../../util/object';

import Resolution from './Resolution';

export default class FrameProvider {
  _frameReadyListeners = new Set();

  /** @property {Map<string, Set>} */
  _mediaByMediaIdentifier = new Map();

  /** @property {Map<string, Set>} */
  _resolution = Resolution.getLowResolution();

  /** @property {Number} */
  _numberOfBufferedFrames = null;

  constructor(resolution) {
    if (Resolution.isResolution(resolution)) {
      this._resolution = resolution;
    }
  }

  get numberOfBufferedFrames() {
    return this._numberOfBufferedFrames;
  }

  get resolution() {
    return this._resolution;
  }

  initialize(mediaIdentifier) {
    if (isEmptyString(mediaIdentifier)) {
      throw new Error('Must provide valid media identifier');
    }

    this._mediaByMediaIdentifier.set(mediaIdentifier, new Set());
  }

  cancel(mediaIdentifier) {
    this._mediaByMediaIdentifier.delete(mediaIdentifier);
  }

  /**
   * Used to initialize fetching FrameRanges of bitmaps.
   * Once frames are ready, they should be dispatched to the frameReadyListeners.
   * @param {FrameProviderPrioritizationInterface} frameProviderPrioritizationInterface
   */
  fetchFrames = (frameProviderPrioritizationInterface) => {
    throw Error(
      `Subclasses of ${FrameProvider.name} need to implement this function`,
    );
  };

  /**
   * Add a callback for when a frame is extracted
   */
  addFrameReadyListener = (listener) => {
    this._frameReadyListeners.add(listener);
  };

  extractionCompleted(mediaIdentifier) {
    this._mediaByMediaIdentifier.delete(mediaIdentifier);
  }

  /**
   * Calls all the frame ready listeners with the rastered bitmap and related info
   * @param {RasteredImageBitmapInterface|CanvasImageBitmapInterface} rasteredImageBitmapInterface
   */
  notifyFrameReadyListeners = (rasteredImageBitmapInterface) => {
    this._frameReadyListeners.forEach((frameReadyListener) => {
      if (isNil(frameReadyListener)) return;
      if (isNotNil(frameReadyListener)) {
        frameReadyListener(rasteredImageBitmapInterface);
      }
    });
  };

  /**
   * Remove a previously assigned frame ready callback function.
   * @param {Function} callback to remove
   */
  removeFrameReadyListener = (listener) => {
    this._frameReadyListeners.delete(listener);
  };

  shouldContinueExtraction = (mediaIdentifier) => {
    return this._mediaByMediaIdentifier.has(mediaIdentifier);
  };

  cleanup = () => {};

  /**
   * Checks if object is an instance of FrameProvider
   *
   * @param inputObject
   * @returns {boolean}
   */
  static isFrameProvider(inputObject) {
    return inputObject instanceof FrameProvider;
  }
}
